/**
 * Promotional-modal frequency policy — pure, storage-free, testable.
 *
 * THE RULE
 *   A browser sees at most ONE promotional SESSION per rolling 24 hours, and
 *   only ever on the homepage. A session is every live campaign shown back to
 *   back — the first after its own delay, each next one as soon as the previous
 *   is closed — and then nothing at all for 24 hours.
 *
 * The previous behaviour had each promo decide for itself: the membership
 * invite was scoped to "/" but the slogan campaign's blank pathPrefix matched
 * every route, so it interrupted people mid-journey on /tours and /book. Both
 * also read their own per-campaign key, so dismissing one left the other free
 * to open seconds later. This module replaces both decisions with one.
 *
 * Everything here is a pure function of (pathname, promos, stored state, now).
 * Nothing touches window or localStorage, so the whole policy is unit-testable
 * and cannot cause a hydration mismatch — the caller reads storage in an
 * effect and passes the values in.
 */

import type { SitePromoRecord } from "@/server/modules/site-promos/shared";

export const DAY_MS = 24 * 60 * 60 * 1000;

/** Floor and fallback, in days. A stored 0 used to mean "never suppressed",
 *  which reopened the modal on every page view; the CMS now rejects anything
 *  below 1, and this clamps legacy rows written before that rule existed. */
export const MIN_PROMO_REPEAT_DAYS = 1;

/**
 * Default window when no promo supplies a usable value.
 *
 * Rolling, not calendar-day: shown 18:45 → next eligible 18:45 tomorrow.
 * Prefer resolvePromoCooldownMs(): the real window is CMS-owned, and this
 * constant is only the fallback for an empty or unusable promo set.
 */
export const PROMO_COOLDOWN_MS = MIN_PROMO_REPEAT_DAYS * DAY_MS;

/**
 * The shared session window, in milliseconds, taken from the CMS.
 *
 * One session shows every eligible campaign, so a single window has to serve
 * them all — and the longest `repeatAfterDays` wins. That way no campaign is
 * ever shown sooner than its own CMS setting allows; the session simply
 * reopens once every campaign is willing to be seen again. Taking the shortest
 * instead would let a 1-day promo drag a 30-day campaign back early.
 *
 * Values below MIN_PROMO_REPEAT_DAYS are clamped rather than honoured.
 */
export function resolvePromoCooldownMs(promos: SitePromoRecord[]): number {
  const days = promos
    .map((promo) => Math.floor(promo.repeatAfterDays))
    .filter((value) => Number.isFinite(value));
  const longest = days.length ? Math.max(...days) : MIN_PROMO_REPEAT_DAYS;
  return Math.max(longest, MIN_PROMO_REPEAT_DAYS) * DAY_MS;
}

/** Shared across every campaign. One session suppresses them all. */
export const PROMO_LAST_SHOWN_KEY = "greenpak:lastPromoShownAt";
/** Informational: which campaign opened the last session. Not used to decide
 *  anything — a session shows every eligible campaign — but it makes the stored
 *  state legible when debugging. */
export const PROMO_LAST_ID_KEY = "greenpak:lastPromoId";

/**
 * The homepage, and only the homepage. A trailing slash is tolerated because
 * some links carry one; nothing deeper ever qualifies.
 */
export function isHomepagePath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return pathname === "/" || pathname === "";
}

/**
 * Parse a stored timestamp. Anything unusable — absent, empty, non-numeric,
 * negative, a future value written by a clock skew or a hand-edited key —
 * reads as "no impression recorded".
 *
 * Failing to null is the safe direction: the worst case is one extra promo,
 * whereas treating corrupt data as a valid recent impression could suppress
 * every campaign forever on that browser.
 */
export function parseImpressionAt(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

/** True while a previous impression still blocks any new one. */
export function isPromoCooldownActive(input: {
  lastShownAt: number | null;
  now: number;
  /** CMS-derived window. Omitted only by callers with no promo set to hand;
   *  they fall back to the default rather than to a hardcoded policy. */
  cooldownMs?: number;
}): boolean {
  const { lastShownAt, now } = input;
  if (lastShownAt == null) return false;
  // A timestamp in the future (clock change) is not evidence of a recent
  // impression, so it does not suppress.
  if (lastShownAt > now) return false;
  return now - lastShownAt < (input.cooldownMs ?? PROMO_COOLDOWN_MS);
}

/** Milliseconds until the next impression is allowed; 0 when eligible now. */
export function msUntilPromoEligible(input: {
  lastShownAt: number | null;
  now: number;
  cooldownMs?: number;
}): number {
  if (!isPromoCooldownActive(input)) return 0;
  return (input.cooldownMs ?? PROMO_COOLDOWN_MS) - (input.now - input.lastShownAt!);
}

/**
 * The campaigns of one session, in the order they will be shown.
 *
 * Deterministic (sortOrder, then key as a tiebreak) so a re-render can never
 * reshuffle the queue under the visitor.
 */
export function orderPromoSession(eligible: SitePromoRecord[]): SitePromoRecord[] {
  return [...eligible].sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key));
}

export type PromoSessionDecision =
  | { show: false; reason: "not-homepage" | "cooldown" | "none-eligible" }
  | { show: true; promos: SitePromoRecord[]; firstDelayMs: number };

/**
 * The whole policy in one call.
 *
 * Order matters. The route is checked first so that an inner-page visit can
 * never consume the cooldown: it returns "not-homepage" without the caller ever
 * reaching the point where an impression is recorded.
 *
 * Returns the FULL session. The caller shows promos[0] after `firstDelayMs`,
 * then each subsequent one as soon as the previous is closed, and records a
 * single impression when the first appears — so the whole run costs the visitor
 * one 24-hour window, not one per campaign.
 */
export function decidePromoSession(input: {
  pathname: string | null | undefined;
  promos: SitePromoRecord[];
  lastShownAt: number | null;
  now: number;
  /** Per-campaign opt-out (CMS repeatAfterDays), layered on top of the shared
   *  cooldown. The shared cooldown remains authoritative for frequency. */
  isCampaignSuppressed?: (promo: SitePromoRecord) => boolean;
}): PromoSessionDecision {
  const { pathname, promos, lastShownAt, now, isCampaignSuppressed } = input;

  if (!isHomepagePath(pathname)) return { show: false, reason: "not-homepage" };

  // Window comes from the CMS, not from this file. Computed over every promo
  // handed in (not just the unsuppressed ones) so that unsuppressing a
  // long-interval campaign cannot retroactively shorten a window already
  // in flight.
  const cooldownMs = resolvePromoCooldownMs(promos);
  if (isPromoCooldownActive({ lastShownAt, now, cooldownMs })) {
    return { show: false, reason: "cooldown" };
  }

  const session = orderPromoSession(promos.filter((promo) => !isCampaignSuppressed?.(promo)));
  if (session.length === 0) return { show: false, reason: "none-eligible" };

  return { show: true, promos: session, firstDelayMs: Math.max(0, session[0].delaySeconds) * 1000 };
}
